import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingCart, Trash2, Plus, Minus, CreditCard, Ticket, Calendar, AlertTriangle, Download, Heart, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { useTicketCart } from '../../context/TicketCartContext';
import { useAuth } from '../../context/AuthContext';
import { safePost } from '../../utils/safeFetch';

import { API_URL } from '../../utils/api';

export default function TicketCartDrawer() {
  const { 
    cartItems, 
    cartOpen, 
    setCartOpen, 
    updateQuantity, 
    removeFromCart, 
    clearCart,
    getCartItemCount,
    validatedCart,
    setValidatedCart,
    validating,
    setValidating
  } = useTicketCart();
  
  const { user } = useAuth();
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutForm, setCheckoutForm] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [processing, setProcessing] = useState(false);
  const [orderComplete, setOrderComplete] = useState(null);
  const [squareLoaded, setSquareLoaded] = useState(false);
  const [card, setCard] = useState(null);
  const [applePay, setApplePay] = useState(null);
  const [applePaySupported, setApplePaySupported] = useState(false);
  const [paymentRequestRef, setPaymentRequestRef] = useState(null);
  const [buyerCoversFees, setBuyerCoversFees] = useState(false);

  // Pre-fill form with user data
  useEffect(() => {
    if (user) {
      setCheckoutForm(prev => ({
        ...prev,
        name: user.name || prev.name,
        email: user.email || prev.email
      }));
    }
  }, [user]);

  const [payments, setPayments] = useState(null);

  // Load Square Web Payments SDK
  useEffect(() => {
    if (showCheckout && !squareLoaded) {
      loadSquareSDK();
    }
  }, [showCheckout]);

  const loadSquareSDK = async () => {
    if (window.Square) {
      initializeSquare();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://web.squarecdn.com/v1/square.js';
    script.onload = () => initializeSquare();
    script.onerror = () => toast.error('Failed to load payment system');
    document.body.appendChild(script);
  };

  const initializeSquare = async () => {
    try {
      const paymentsInstance = window.Square.payments(
        process.env.REACT_APP_SQUARE_APP_ID || 'sq0idp-RAcpI-KE3j4SU0smO5Zn8g',
        process.env.REACT_APP_SQUARE_LOCATION_ID || 'LQQ9GGWKR8766'
      );

      // Initialize Card payment
      const cardInstance = await paymentsInstance.card();
      await cardInstance.attach('#card-container');
      setCard(cardInstance);
      setPayments(paymentsInstance);
      setSquareLoaded(true);
      
      // Apple Pay temporarily disabled due to iOS Safari compatibility issues
      // Card payments work correctly
      // TODO: Re-enable Apple Pay after debugging with Safari developer tools
      setApplePaySupported(false);
      console.log('Apple Pay disabled - use card payment');
      
      /*
      // Initialize Apple Pay if supported
      try {
        // Create a payment request for Apple Pay initialization
        const paymentRequest = paymentsInstance.paymentRequest({
          countryCode: 'GB',
          currencyCode: 'GBP',
          total: {
            amount: '1.00',
            label: 'Line Dance Central',
          },
        });
        
        setPaymentRequestRef(paymentRequest);
        
        const applePayInstance = await paymentsInstance.applePay(paymentRequest);
        setApplePay(applePayInstance);
        setApplePaySupported(true);
        console.log('Apple Pay initialized successfully');
      } catch (applePayError) {
        // Apple Pay not available - this is normal on non-Apple devices
        console.log('Apple Pay not available:', applePayError.message || applePayError);
        setApplePaySupported(false);
      }
      */
    } catch (error) {
      console.error('Square init error:', error);
      toast.error('Failed to initialize payment form');
    }
  };

  // Validate cart when opened or items change
  useEffect(() => {
    if (cartOpen && cartItems.length > 0 && !validatedCart) {
      validateCart();
    }
  }, [cartOpen, cartItems]);

  const validateCart = async (coversFees = buyerCoversFees) => {
    if (cartItems.length === 0) return;
    
    setValidating(true);
    console.log('[Cart] Validating with buyer_covers_fees:', coversFees);
    
    try {
      const requestBody = {
        items: cartItems.map(item => ({
          event_id: item.event_id,
          ticket_type_id: item.ticket_type_id,
          quantity: item.quantity
        })),
        buyer_covers_fees: coversFees
      };
      console.log('[Cart] Request body:', JSON.stringify(requestBody));
      
      const res = await safePost(`${API_URL}/api/tickets/cart/validate`, requestBody);

      console.log('[Cart] Response:', res);

      if (res.networkError) {
        console.error('Network error validating cart:', res.data);
        toast.error('Connection error. Please check your internet.');
        return;
      }

      if (res.ok) {
        console.log('[Cart] Validated cart data:', res.data);
        console.log('[Cart] has_charity_event:', res.data.has_charity_event);
        console.log('[Cart] platform_fees:', res.data.platform_fees);
        console.log('[Cart] total_amount:', res.data.total_amount);
        console.log('[Cart] debug_info:', res.data.debug_info);
        console.log('[Cart] items:', res.data.items);
        if (res.data.items?.[0]) {
          console.log('[Cart] First item is_charity_event:', res.data.items[0].is_charity_event);
          console.log('[Cart] First item is_fee_exempt:', res.data.items[0].is_fee_exempt);
          console.log('[Cart] First item absorb_fees:', res.data.items[0].absorb_fees);
          console.log('[Cart] First item line_fees:', res.data.items[0].line_fees);
        }
        setValidatedCart(res.data);
      } else {
        toast.error(res.data?.detail || 'Failed to validate cart');
      }
    } catch (error) {
      console.error('Cart validation error:', error);
      toast.error('Error validating cart. Please try again.');
    } finally {
      setValidating(false);
    }
  };

  const handleCheckout = async () => {
    if (!checkoutForm.name || !checkoutForm.email) {
      toast.error('Please fill in your name and email');
      return;
    }

    if (!card || !payments) {
      toast.error('Payment form not ready. Please wait...');
      return;
    }

    setProcessing(true);
    try {
      // Tokenize card
      const tokenResult = await card.tokenize();
      if (tokenResult.status !== 'OK') {
        const cardError = tokenResult.errors?.[0]?.message || 'Please check your card details';
        toast.error(cardError);
        setProcessing(false);
        return;
      }

      // Calculate amount for verification
      const amount = validatedCart?.total_amount || cartItems.reduce((t, i) => t + i.price * i.quantity, 0);
      // Amount should be in POUNDS for verifyBuyer (e.g., "2.00" for £2)
      const amountInPounds = amount.toFixed(2);

      // Perform 3D Secure verification (required for UK/EU cards)
      let verificationToken = null;
      try {
        const verificationDetails = {
          amount: amountInPounds,
          currencyCode: 'GBP',
          intent: 'CHARGE',
          billingContact: {
            givenName: checkoutForm.name.split(' ')[0] || checkoutForm.name,
            familyName: checkoutForm.name.split(' ').slice(1).join(' ') || '',
            email: checkoutForm.email,
            phone: checkoutForm.phone || undefined,
          }
        };

        const verificationResult = await payments.verifyBuyer(
          tokenResult.token,
          verificationDetails
        );

        if (verificationResult) {
          verificationToken = verificationResult.token;
        }
      } catch (verifyError) {
        console.error('3D Secure verification error:', verifyError);
        // Continue without verification token - some cards don't require it
        // The backend will handle if verification is strictly required
      }

      // Build checkout payload
      const checkoutPayload = {
        cart_items: cartItems.map(item => ({
          event_id: item.event_id,
          ticket_type_id: item.ticket_type_id,
          quantity: item.quantity
        })),
        customer_name: checkoutForm.name,
        customer_email: checkoutForm.email,
        customer_phone: checkoutForm.phone || null,
        source_id: tokenResult.token,
        verification_token: verificationToken,
        buyer_covers_fees: buyerCoversFees
      };

      // Process checkout using safePost to avoid Safari body issues
      const res = await safePost(`${API_URL}/api/tickets/checkout`, checkoutPayload);

      // Check for network errors first
      if (res.networkError) {
        console.error('Network error during checkout:', res.data);
        toast.error('Connection error. Please check your internet and try again.');
        return;
      }

      // Check for response parsing errors
      if (res.parseError && !res.ok) {
        console.error('Response parsing error:', res.parseError);
        toast.error(res.data?.detail || 'Server error. Please try again.');
        return;
      }

      if (res.ok && res.data?.order) {
        setOrderComplete(res.data.order);
        clearCart();
        toast.success('Tickets purchased successfully!');
      } else {
        // Extract error message from response
        const errorMsg = res.data?.detail || res.data?.message || res.data?.error || 'Checkout failed. Please try again.';
        console.error('Checkout error response:', res);
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('Checkout exception:', error);
      toast.error(error.message || 'Payment failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  // Handle Apple Pay checkout
  const handleApplePayCheckout = async () => {
    // Use stored Apple Pay instance and update the payment amount
    try {
      if (!applePay || !payments || !validatedCart || !paymentRequestRef) {
        toast.error('Apple Pay not ready. Please use card payment.');
        return;
      }

      if (!checkoutForm.name || !checkoutForm.email) {
        toast.error('Please fill in your name and email first');
        return;
      }
      
      // Validate cart items exist
      if (!validatedCart.items || validatedCart.items.length === 0) {
        toast.error('Your cart is empty. Please add items first.');
        return;
      }

      setProcessing(true);
      
      // Update the payment request with the correct amount
      const actualAmount = validatedCart.total_amount?.toFixed(2) || '0.00';
      console.log('[ApplePay] Updating payment request with amount:', actualAmount);
      
      try {
        paymentRequestRef.update({
          total: {
            amount: actualAmount,
            label: 'Line Dance Central Tickets',
          },
        });
      } catch (updateError) {
        console.error('Payment request update error:', updateError);
        // Continue anyway, the amount might be wrong but let's try
      }
      
      // Tokenize using the stored Apple Pay instance
      let tokenResult;
      try {
        tokenResult = await applePay.tokenize();
        console.log('[ApplePay] Tokenize result:', tokenResult?.status);
      } catch (tokenizeError) {
        console.error('Apple Pay tokenize error:', tokenizeError);
        toast.info('Apple Pay cancelled. You can try again or use card payment.');
        setProcessing(false);
        return;
      }
      
      if (!tokenResult || tokenResult.status !== 'OK') {
        console.error('Apple Pay tokenize failed:', tokenResult);
        if (tokenResult?.errors) {
          tokenResult.errors.forEach(e => console.error(e));
        }
        toast.error('Apple Pay payment cancelled or failed.');
        setProcessing(false);
        return;
      }

      console.log('[ApplePay] Token received, submitting to backend...');
      
      // Prepare cart items safely
      const cartItems = validatedCart.items.map(item => ({
        ticket_type_id: item.ticket_type_id,
        quantity: item.quantity
      }));
      
      // Submit payment to backend
      const res = await safePost(`${API_URL}/api/tickets/checkout`, {
        cart_items: cartItems,
        customer_name: checkoutForm.name,
        customer_email: checkoutForm.email,
        customer_phone: checkoutForm.phone || null,
        source_id: tokenResult.token,
        verification_token: null,
        buyer_covers_fees: buyerCoversFees
      });

      if (res.networkError) {
        console.error('Network error during Apple Pay checkout:', res.data);
        toast.error('Connection error. Please check your internet and try again.');
        setProcessing(false);
        return;
      }

      if (res.ok && res.data?.order) {
        setOrderComplete(res.data.order);
        clearCart();
        toast.success('Tickets purchased successfully with Apple Pay!');
      } else {
        const errorMsg = res.data?.detail || res.data?.message || 'Checkout failed. Please try again.';
        console.error('Apple Pay checkout error:', res);
        toast.error(errorMsg);
      }
      setProcessing(false);
    } catch (error) {
      console.error('Apple Pay critical exception:', error);
      toast.error('Something went wrong. Please try card payment instead.');
      setProcessing(false);
    }
  };

  const handleClose = () => {
    setCartOpen(false);
    setShowCheckout(false);
    if (orderComplete) {
      setOrderComplete(null);
    }
  };

  // Group items by event for display
  const groupedItems = cartItems.reduce((acc, item) => {
    if (!acc[item.event_id]) {
      acc[item.event_id] = {
        event_id: item.event_id,
        event_title: item.event_title,
        items: []
      };
    }
    acc[item.event_id].items.push(item);
    return acc;
  }, {});

  return (
    <AnimatePresence>
      {cartOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            onClick={handleClose}
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-slate-900 border-l border-slate-800 z-50 flex flex-col shadow-2xl"
            data-testid="ticket-cart-drawer"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-800">
              <div className="flex items-center gap-3">
                <ShoppingCart className="w-5 h-5 text-violet-400" />
                <h2 className="text-lg font-bold text-white">
                  {orderComplete ? 'Order Complete!' : showCheckout ? 'Checkout' : 'Your Basket'}
                </h2>
              </div>
              <button
                onClick={handleClose}
                className="p-2 text-slate-400 hover:text-white transition-colors"
                data-testid="close-cart-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {/* Order Complete State */}
              {orderComplete ? (
                <div className="p-6 space-y-6">
                  <div className="text-center py-4">
                    <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Ticket className="w-8 h-8 text-emerald-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">Thank You!</h3>
                    <p className="text-slate-400">
                      Your tickets have been sent to <span className="text-white">{orderComplete.customer_email}</span>
                    </p>
                  </div>

                  <div className="bg-slate-800/50 rounded-xl p-4 space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Order Reference</span>
                      <span className="text-white font-mono">{orderComplete.order_id}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Total Paid</span>
                      <span className="text-emerald-400 font-bold">£{orderComplete.total_amount?.toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Download All Tickets Button */}
                  <div className="flex gap-2">
                    <a
                      href={`${API_URL}/api/tickets/download/order/${orderComplete.order_id}/pdf?email=${encodeURIComponent(orderComplete.customer_email)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1"
                    >
                      <Button className="w-full bg-slate-700 hover:bg-slate-600 text-white" data-testid="download-all-tickets-pdf">
                        <Download className="w-4 h-4 mr-2" />
                        Download All Tickets (PDF)
                      </Button>
                    </a>
                  </div>

                  {/* Show tickets */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-white">Your Tickets</h4>
                    {orderComplete.tickets?.map(ticket => (
                      <div 
                        key={ticket.ticket_id}
                        className="bg-slate-800 rounded-xl p-4 border border-slate-700"
                      >
                        <div className="flex items-center gap-3">
                          <div className="bg-white p-2 rounded-lg">
                            <img 
                              src={`data:image/png;base64,${ticket.qr_code}`}
                              alt="Ticket QR"
                              className="w-16 h-16"
                            />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-white">{ticket.event_title}</p>
                            <p className="text-violet-400 text-sm">{ticket.ticket_type_name}</p>
                            <p className="text-slate-400 text-xs font-mono mt-1">{ticket.ticket_code}</p>
                          </div>
                        </div>
                        {/* Individual ticket download */}
                        <div className="mt-3 flex gap-2">
                          <a
                            href={`${API_URL}/api/tickets/download/${ticket.ticket_id}/pdf`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1"
                          >
                            <Button variant="outline" size="sm" className="w-full text-xs border-slate-600 text-slate-300 hover:bg-slate-700">
                              <Download className="w-3 h-3 mr-1" />
                              PDF
                            </Button>
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>

                  <Button
                    onClick={handleClose}
                    className="w-full bg-violet-600 hover:bg-violet-500"
                  >
                    Done
                  </Button>
                </div>
              ) : showCheckout ? (
                /* Checkout Form - Reorganized for better mobile UX */
                <div className="p-6 space-y-5">
                  {/* Order Summary - Compact */}
                  <div className="bg-slate-800/50 rounded-xl p-3 space-y-1">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400 text-sm">{validatedCart?.total_tickets || cartItems.reduce((t, i) => t + i.quantity, 0)} tickets</span>
                      <span className="text-emerald-400 font-bold">£{validatedCart?.total_amount?.toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Customer Details - Name & Email only, more compact */}
                  <div className="space-y-3">
                    <h4 className="font-medium text-white text-sm">Your Details</h4>
                    <div className="grid grid-cols-1 gap-3">
                      <div>
                        <Label className="text-slate-300 text-xs">Full Name *</Label>
                        <Input
                          value={checkoutForm.name}
                          onChange={e => setCheckoutForm(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="John Smith"
                          className="bg-slate-800 border-slate-700 h-10"
                          data-testid="checkout-name"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300 text-xs">Email *</Label>
                        <Input
                          type="email"
                          value={checkoutForm.email}
                          onChange={e => setCheckoutForm(prev => ({ ...prev, email: e.target.value }))}
                          placeholder="you@example.com"
                          className="bg-slate-800 border-slate-700 h-10"
                          data-testid="checkout-email"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300 text-xs">Phone (for SMS tickets)</Label>
                        <Input
                          type="tel"
                          value={checkoutForm.phone}
                          onChange={e => setCheckoutForm(prev => ({ ...prev, phone: e.target.value }))}
                          placeholder="07123 456789"
                          className="bg-slate-800 border-slate-700 h-10"
                          data-testid="checkout-phone"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Add your phone to receive tickets via SMS (recommended for Apple/iCloud users)
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Card Payment - Now prominent with visual emphasis */}
                  <div className="space-y-3">
                    {/* Apple Pay Button - Show if supported */}
                    {applePaySupported && (
                      <div className="space-y-3">
                        <Button
                          onClick={handleApplePayCheckout}
                          disabled={processing || !validatedCart}
                          className="w-full h-14 bg-white hover:bg-gray-100 text-black font-semibold rounded-xl flex items-center justify-center gap-2 border border-gray-300 shadow-sm"
                          data-testid="apple-pay-button"
                        >
                          {processing ? (
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black"></div>
                          ) : (
                            <>
                              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M17.72 6.56c-.98-1.12-2.27-1.78-3.52-1.78-1.65 0-2.35.8-3.5.8-1.17 0-2.06-.8-3.47-.8-1.41 0-2.91.86-3.86 2.32-1.35 2.07-.89 5.97 1.52 9.3.87 1.21 2.03 2.56 3.53 2.58 1.41.02 1.86-.91 3.49-.92 1.63-.01 2.04.93 3.45.91 1.5-.02 2.72-1.52 3.59-2.73.62-.86.85-1.3 1.35-2.27-3.54-1.35-4.11-6.42-.58-8.41z"/>
                                <path d="M14.5 3.5c.75-.96 1.32-2.32 1.12-3.7-1.28.09-2.78.91-3.65 1.97-.78.95-1.42 2.32-1.17 3.67 1.4.04 2.86-.77 3.7-1.94z"/>
                              </svg>
                              Pay £{validatedCart?.total_amount?.toFixed(2) || '0.00'}
                            </>
                          )}
                        </Button>
                        <div className="flex items-center gap-3">
                          <div className="flex-1 h-px bg-slate-700"></div>
                          <span className="text-slate-500 text-xs">or pay with card</span>
                          <div className="flex-1 h-px bg-slate-700"></div>
                        </div>
                      </div>
                    )}
                    
                    <h4 className="font-medium text-white text-sm flex items-center gap-2">
                      <CreditCard className="w-4 h-4 text-violet-400" />
                      Card Details *
                    </h4>
                    <div 
                      id="card-container" 
                      className="bg-slate-800 p-4 rounded-xl border-2 border-violet-500/50 min-h-[80px]"
                    >
                      {!squareLoaded && (
                        <div className="flex items-center justify-center py-4">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-violet-500"></div>
                          <span className="text-slate-400 text-sm ml-2">Loading payment form...</span>
                        </div>
                      )}
                    </div>
                    <p className="text-slate-400 text-xs text-center">Secure payment powered by Square</p>
                  </div>
                </div>
              ) : cartItems.length === 0 ? (
                /* Empty Cart */
                <div className="flex flex-col items-center justify-center h-full py-12 px-6">
                  <ShoppingCart className="w-16 h-16 text-slate-700 mb-4" />
                  <h3 className="text-lg font-semibold text-slate-400 mb-2">Your basket is empty</h3>
                  <p className="text-slate-400 text-center text-sm">
                    Browse events and add tickets to get started
                  </p>
                </div>
              ) : (
                /* Cart Items */
                <div className="p-4 space-y-4">
                  {validating && (
                    <div className="bg-violet-500/10 border border-violet-500/30 rounded-lg p-3 flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-violet-500"></div>
                      <span className="text-violet-400 text-sm">Checking availability...</span>
                    </div>
                  )}

                  {Object.values(groupedItems).map(group => (
                    <div key={group.event_id} className="bg-slate-800/50 rounded-xl overflow-hidden">
                      {/* Event Header */}
                      <div className="px-4 py-3 bg-slate-800 border-b border-slate-700">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-violet-400" />
                          <span className="font-medium text-white text-sm truncate">
                            {group.event_title}
                          </span>
                        </div>
                      </div>

                      {/* Ticket Items */}
                      <div className="p-3 space-y-3">
                        {group.items.map(item => (
                          <div 
                            key={`${item.event_id}-${item.ticket_type_id}`}
                            className="flex items-center justify-between"
                          >
                            <div className="flex-1 min-w-0">
                              <p className="text-white text-sm font-medium truncate">
                                {item.ticket_type_name}
                              </p>
                              <p className="text-emerald-400 text-sm">
                                £{item.price.toFixed(2)} each
                              </p>
                            </div>

                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => updateQuantity(item.event_id, item.ticket_type_id, item.quantity - 1)}
                                className="w-7 h-7 rounded-full bg-slate-700 hover:bg-slate-600 flex items-center justify-center"
                              >
                                <Minus className="w-3 h-3 text-white" />
                              </button>
                              <span className="w-6 text-center text-white font-medium">
                                {item.quantity}
                              </span>
                              <button
                                onClick={() => updateQuantity(item.event_id, item.ticket_type_id, item.quantity + 1)}
                                className="w-7 h-7 rounded-full bg-violet-600 hover:bg-violet-500 flex items-center justify-center"
                              >
                                <Plus className="w-3 h-3 text-white" />
                              </button>
                              <button
                                onClick={() => removeFromCart(item.event_id, item.ticket_type_id)}
                                className="w-7 h-7 rounded-full hover:bg-red-500/20 flex items-center justify-center ml-1"
                              >
                                <Trash2 className="w-3.5 h-3.5 text-red-400" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}

                  {/* Clear All */}
                  <button
                    onClick={clearCart}
                    className="text-slate-400 hover:text-red-400 text-sm transition-colors"
                  >
                    Clear basket
                  </button>
                </div>
              )}
            </div>

            {/* Footer */}
            {!orderComplete && cartItems.length > 0 && (
              <div className="border-t border-slate-800 p-4 bg-slate-900/80">
                {/* Charity Event - Cover Fees Option */}
                {validatedCart?.has_charity_event && !buyerCoversFees && (
                  <div className="mb-4 p-4 bg-gradient-to-r from-emerald-600 to-green-600 rounded-xl shadow-lg">
                    <div 
                      className="flex items-center gap-3 cursor-pointer"
                      onClick={() => {
                        setBuyerCoversFees(true);
                        validateCart(true);
                      }}
                    >
                      <div className="w-6 h-6 rounded border-2 border-white flex items-center justify-center bg-white/20 hover:bg-white/30 transition-colors">
                        {buyerCoversFees && <Check className="w-4 h-4 text-white" />}
                      </div>
                      <div className="flex-1">
                        <p className="text-white font-bold text-base flex items-center gap-2">
                          <Heart className="w-5 h-5 fill-white" />
                          Cover the 50p booking fee
                        </p>
                        <p className="text-emerald-100 text-sm mt-1">
                          100% of £{validatedCart?.subtotal?.toFixed(2)} goes to {validatedCart?.charity_events?.[0]?.charity_name || 'charity'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {buyerCoversFees && validatedCart?.has_charity_event && (
                  <div className="mb-4 p-3 bg-emerald-900/50 border border-emerald-700 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-emerald-300">
                        <Heart className="w-4 h-4 fill-current" />
                        <span className="text-sm font-medium">Thank you for covering the fees!</span>
                      </div>
                      <button 
                        onClick={() => {
                          setBuyerCoversFees(false);
                          validateCart(false);
                        }}
                        className="text-xs text-emerald-400 hover:text-emerald-300 underline"
                      >
                        Undo
                      </button>
                    </div>
                    <p className="text-emerald-400/70 text-xs mt-1">
                      100% of £{validatedCart?.subtotal?.toFixed(2)} goes to {validatedCart?.charity_events?.[0]?.charity_name || 'charity'}
                    </p>
                  </div>
                )}

                {/* Pricing Summary */}
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Subtotal ({getCartItemCount()} tickets)</span>
                    <span className="text-white">
                      £{validatedCart?.subtotal?.toFixed(2) || cartItems.reduce((t, i) => t + i.price * i.quantity, 0).toFixed(2)}
                    </span>
                  </div>
                  {validatedCart?.platform_fees > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">
                        Booking fees {buyerCoversFees && <span className="text-emerald-400">(you&apos;re covering)</span>}
                      </span>
                      <span className="text-white">£{validatedCart.platform_fees.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-lg font-bold pt-2 border-t border-slate-700">
                    <span className="text-white">Total</span>
                    <span className="text-emerald-400">
                      £{validatedCart?.total_amount?.toFixed(2) || cartItems.reduce((t, i) => t + i.price * i.quantity, 0).toFixed(2)}
                    </span>
                  </div>
                </div>

                {showCheckout ? (
                  <div className="space-y-2">
                    <Button
                      onClick={handleCheckout}
                      disabled={processing || !squareLoaded}
                      className="w-full bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 text-white font-semibold py-3"
                      data-testid="pay-now-btn"
                    >
                      {processing ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Processing...
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4 mr-2" />
                          Pay £{validatedCart?.total_amount?.toFixed(2)}
                        </>
                      )}
                    </Button>
                    <Button
                      onClick={() => setShowCheckout(false)}
                      variant="ghost"
                      className="w-full text-slate-400"
                      disabled={processing}
                    >
                      Back to basket
                    </Button>
                  </div>
                ) : (
                  <Button
                    onClick={() => {
                      validateCart();
                      setShowCheckout(true);
                    }}
                    disabled={validating}
                    className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white font-semibold py-3"
                    data-testid="checkout-btn"
                  >
                    Checkout
                  </Button>
                )}
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
